import { ComponentProps, type JSX, type Ref } from 'react';
import { useMediaQuery } from 'react-responsive';
import theme from 'src/utils/theme';
import { isRomanizationLabel } from 'src/api/settings/learningToggles';
import { Direction, TestPromptType } from 'src/api/learningSessions/types';
import { romanizationTextAttrs } from 'src/utils/pinyinFont';
import { VideoPlayerHandle } from 'src/components';
import {
  FullscreenBackgroundVideo,
  TEST_VIDEO_OBJECT_POSITION,
} from 'src/apps/LearningSession/Common/learnableCards/PresentationLearnableCard/FullscreenBackgroundVideo';
import * as S from './styles';

interface BasePrompt {
  promptType: TestPromptType;
  direction: Direction; // for analytics
}

interface ImagePrompt extends BasePrompt {
  promptType: 'Image';
  image: string;
}

export interface TextPrompt extends BasePrompt {
  promptType: 'Text';
  text: string;
  showBigger: boolean;
  label?: string;
}

export interface VideoPrompt extends BasePrompt {
  promptType: 'Video';
  video: string;
  /** Actor IDs for the selected video URL, when the API provides them. */
  actorIds?: number[];
}

interface AudioPrompt extends BasePrompt {
  promptType: 'Audio';
  audio: string;
}

export type TestPrompt = ImagePrompt | TextPrompt | VideoPrompt | AudioPrompt;

interface PromptProps {
  prompt: TestPrompt;
  videoRef?: Ref<VideoPlayerHandle>;
}

// Sorry for the weird component name; avoiding clash with the type above
const PromptDisplay = ({ prompt, videoRef }: PromptProps): JSX.Element => {
  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });
  switch (prompt.promptType) {
    case 'Text': {
      const hasLargeWord = prompt.text.split(' ').some(word => word.length > 10);

      let textSize: ComponentProps<typeof S.PromptText>['$textSize'] = prompt.showBigger
        ? 'large'
        : 'medium';
      if (hasLargeWord && isMobile) {
        textSize = 'small';
      }

      return (
        <S.PromptText
          $textSize={textSize}
          data-testid="learn-prompt-text"
          {...(prompt.label && isRomanizationLabel(prompt.label)
            ? romanizationTextAttrs(prompt.text)
            : undefined)}
        >
          <span dir="auto">{prompt.text}</span>
        </S.PromptText>
      );
    }
    case 'Audio':
      return (
        <S.PromptAudio
          data-testid="promptAudio"
          key={prompt.audio}
          larger
          playOnKeyboardShortcut
          autoPlay
          src={prompt.audio}
        />
      );
    case 'Image':
      return <S.PromptImage data-testid="promptImg" src={prompt.image} alt="" />;
    case 'Video':
      return (
        <FullscreenBackgroundVideo
          key={prompt.video}
          ref={videoRef}
          src={prompt.video}
          objectPosition={TEST_VIDEO_OBJECT_POSITION}
          data-testid="testVideoContainer"
          overlayTestId="testVideoOverlay"
        />
      );
  }
};

export default PromptDisplay;
